import { useEffect, useState } from "react";
import { MapPin, Phone, Mail, IndianRupee } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/portal/widgets";
import api from "@/lib/api";
import { toast } from "sonner";

const STAGES = [
  { key: "submitted", label: "Submitted", color: "bg-slate-100 text-slate-700" },
  { key: "under_review", label: "Under Review", color: "bg-amber-100 text-amber-700" },
  { key: "approved", label: "Approved", color: "bg-sky-100 text-sky-700" },
  { key: "onboarded", label: "Onboarded", color: "bg-yellow-200 text-yellow-700" },
];
const NEXT = { submitted: "under_review", under_review: "approved", approved: "onboarded" };

export default function FranchisePipeline() {
  const [apps, setApps] = useState([]);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/admin/enquiries").then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const move = async (app, status) => {
    try {
      await api.patch(`/admin/enquiries/${app.id}`, { status, note: note || `Moved to ${status}` });
      toast.success(`${app.full_name} → ${status.replace("_", " ")}`);
      setNote(""); setActive(null); load();
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Franchise Pipeline" subtitle="Review and progress franchise applications through the pipeline." />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {STAGES.map((st) => {
          const items = apps.filter((a) => a.status === st.key);
          return (
            <div key={st.key} data-testid={`column-${st.key}`} className="bg-slate-100/60 dark:bg-slate-900 rounded-2xl p-3">
              <div className="flex items-center justify-between px-2 py-2">
                <h3 className="font-heading font-semibold text-sm">{st.label}</h3>
                <Badge className={st.color}>{items.length}</Badge>
              </div>
              <div className="space-y-3 mt-2 min-h-[100px]">
                {items.map((a) => (
                  <Card key={a.id} data-testid={`app-card-${a.id}`} onClick={() => setActive(a)}
                    className="p-4 cursor-pointer border-slate-200/80 dark:border-slate-800 hover:shadow-md transition-all bg-white dark:bg-slate-950">
                    <p className="font-heading font-semibold text-sm">{a.full_name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {a.city}, {a.state}</p>
                    <p className="text-xs text-yellow-700 font-medium mt-2 flex items-center gap-1"><IndianRupee className="w-3 h-3" />{a.investment_capacity}</p>
                    <p className="font-mono text-[10px] text-muted-foreground mt-2">{a.reference}</p>
                  </Card>
                ))}
                {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No applications</p>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg" data-testid="app-detail-modal">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{active.full_name}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" /> {active.email}</p>
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /> {active.phone}</p>
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /> {active.city}, {active.state}</p>
                  <p className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-muted-foreground" /> {active.investment_capacity}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div><p className="text-muted-foreground text-xs">Store Size</p><p>{active.store_size_sqft}</p></div>
                  <div><p className="text-muted-foreground text-xs">Experience</p><p>{active.experience}</p></div>
                  <div><p className="text-muted-foreground text-xs">Timeline</p><p>{active.timeline}</p></div>
                  <div><p className="text-muted-foreground text-xs">Reference</p><p className="font-mono text-xs">{active.reference}</p></div>
                </div>
                {active.message && <div className="pt-2 border-t"><p className="text-muted-foreground text-xs">Message</p><p>{active.message}</p></div>}
                <Textarea placeholder="Add a review note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="review-note" />
                <div className="flex gap-2 pt-2">
                  {NEXT[active.status] && (
                    <Button onClick={() => move(active, NEXT[active.status])} data-testid="advance-btn" className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-slate-900 capitalize">
                      Advance to {NEXT[active.status].replace("_", " ")}
                    </Button>
                  )}
                  {active.status !== "rejected" && active.status !== "onboarded" && (
                    <Button onClick={() => move(active, "rejected")} data-testid="reject-btn" variant="outline" className="border-red-500 text-red-600 hover:bg-red-50">Reject</Button>
                  )}
                </div>
                {active.status === "onboarded" && <p className="text-xs text-yellow-700 bg-yellow-100 dark:bg-yellow-950 p-3 rounded-lg">Onboarded — franchisee account & store created. Login: {active.email} / Franchise@123</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
